/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE. 
*/

import { BlurredImage, CosmosAsset, CosmosImage, CosmosLink } from "@components/elements/atoms";
import { css, cx } from "@panda/css";
import { CARD_HEIGHT, CARD_WIDTH } from "./UTCards.section";
import { UtCardFragment } from "@cosmos/hygraph-schema";

export interface UTCardProps {
	card?: UtCardFragment;
	alignment?: "top" | "bottom";
}

const styles = css({
	height: "var(--card-height)",
	transform: "translate3d(0, 0, 0)",
	flex: "0 0 var(--slide-size)",
	minWidth: 0,
	paddingInlineStart: "var(--slide-spacing)",
	width: `var(--slide-size)`,
	//content-box needed to get the cards at exact heights
	boxSizing: "content-box",
	"& img": {
		borderRadius: "eds.borderRadius.24",
	},
	//for attribution corner correction
	"--asset-border-radius": `24px`,
	"&.bottom": {
		paddingBlockStart: `var(--slide-offset)`,
	},
	_hover: {
		"& a .card-image": {
			transform: "scale3d(1.05, 1.05, 1.05)",
		},
		"& a .card-overlay": {
			opacity: 0.6,
		},
		"& a .card-logo img, & a .card-logo svg": {
			transform: "scale3d(1.08, 1.08, 1.08)",
		},
	},
});

const blurredStyles = css({
	position: "absolute",
	pointerEvents: "none",
	top: 0,
	bottom: 0,
	right: 0,
	left: 0,
	width: "100%",
	smDown: {
		height: "100%",
	},
	"& .blurred-image, img, video": {
		smDown: {
			height: "100%",
		},
	},
	display: "none",
});

const cardStyles = css({
	w: "full",
	h: "full",
	objectFit: "cover",
	transition: "transform token(animations.eds.duration.125) token(animations.eds.easing.easeInOutQuad)",
});

const overlayStyles = css({
	background: "radial-gradient(circle,rgba(0, 0, 0, 1) 0%, rgba(255, 255, 255, 0) 70%)",
	opacity: 0.35,
	position: "absolute",
	zIndex: 2,
	top: 0,
	bottom: 0,
	left: 0,
	right: 0,
	transition: "opacity token(animations.eds.duration.125) token(animations.eds.easing.easeInOutQuad)",
	borderRadius: "eds.borderRadius.24",
});

const logoStyles = css({
	position: "absolute",
	zIndex: 3,
	top: 0,
	bottom: 0,
	left: 0,
	right: 0,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",

	"& img, & svg": {
		transition: "transform token(animations.eds.duration.125) token(animations.eds.easing.easeInOutQuad)",
	},
});

export const UTCard = ({ card, alignment }: UTCardProps): React.JSX.Element | null => {
	if (!card?.cardImage?.url) {
		return null;
	}

	const cardEl = (
		<div
			className={css({
				position: "relative",
				w: "full",
				h: "full",
				overflow: "hidden",
				borderRadius: "eds.borderRadius.24",
			})}
		>
			<CosmosAsset
				asset={card.cardImage}
				className={cx("card-image", cardStyles)}
				attributionClassName={css({
					overflow: "hidden",
					borderRadius: "eds.borderRadius.24",
				})}
			/>
			{card.logoImage ? (
				<>
					<div className={cx("card-overlay", overlayStyles)} />
					<div className={cx("card-logo", logoStyles)}>
						<div className={css({ width: "66%", margin: "auto" })}>
							<CosmosImage
								src={card.logoImage.url}
								alt={card.logoImage.altText || ""}
								className={css({ w: "full" })}
								unoptimized
							/>
						</div>
					</div>
				</>
			) : null}
		</div>
	);

	return (
		<div className={cx("ut-card", styles, alignment)}>
			<div className={blurredStyles}>
				<BlurredImage blurPixels={50} image={card.cardImage} height={CARD_HEIGHT} width={CARD_WIDTH} opacity={0.5} />
			</div>

			{card.href ? (
				<CosmosLink href={card.href} title={card.label || ""}>
					{cardEl}
				</CosmosLink>
			) : (
				cardEl
			)}
		</div>
	);
};
